/**
 * SSO Confirmation Modal constants and helpers.
 *
 * Used when the user changes a radio option on the SSO management form. Before
 * applying the change, a confirmation modal is shown with text that depends on
 * the target values (ssoEnabled and ssoUsers). Use {@link getSsoConfirmationModalContent}
 * to get the correct content for the target values.
 *
 * @see SSOConfirmationModal - The modal component that renders this content
 * @see SSOManagementCard - The form that triggers the modal on radio change
 */

import {RoleNameEnum} from "../../utils/types/common";
import {SSO_MANAGEMENT_FORM_FIELDS, SSO_USERS} from "./constants";

/** Content passed to SSOConfirmationModal: title, body text, and confirm button label. */
export type SsoConfirmationModalContent = Readonly<{
  title: string;
  body: string;
  confirmButtonLabel: string;
}>;

/** Static texts for each confirmation scenario. Keys match form state combinations. */
export const SSO_CONFIRMATION_MODAL_TEXTS: Record<
  "enable" | "disable" | "no_users" | "all_users" | "all_except_org_admin",
  SsoConfirmationModalContent
> = {
  enable: {
    title: "Enable SSO for your organisation",
    body: "Are you sure you want to enable SSO for your organization?",
    confirmButtonLabel: "Yes enable SSO",
  },
  disable: {
    title: "Disable SSO for your organisation",
    body: "Are you sure you want to disable SSO for your organization?",
    confirmButtonLabel: "Yes disable SSO",
  },
  no_users: {
    title: "Choose for no users of this organization to sign in with SSO",
    body: "Are you sure you want no users of this organization to sign in with SSO?",
    confirmButtonLabel: "Yes continue",
  },
  all_users: {
    title: "Choose for all users of this organization to sign in with SSO",
    body: "Are you sure you want all users of this organization to sign in with SSO?",
    confirmButtonLabel: "Yes continue",
  },
  all_except_org_admin: {
    title:
      "Choose for all users of this organization, except Organization Admins to sign in with SSO",
    body: "Are you sure you want all users of this organization, except Organization Admins to sign in with SSO?",
    confirmButtonLabel: "Yes continue",
  },
};

export type SsoFormValues = Readonly<{
  ssoEnabled: boolean;
  ssoUsers: SSO_USERS | null;
}>;

/** Form shape for Formik (field ssoExcludeRequiredRoles holds ssoUsers value) */
export type SsoFormikValues = Readonly<{
  [SSO_MANAGEMENT_FORM_FIELDS.SSO_ENABLED]: boolean;
  [SSO_MANAGEMENT_FORM_FIELDS.SSO_USERS]: SSO_USERS;
}>;

/**
 * Returns the modal content for the given form values.
 *
 * Flow: first enable SSO (generic), then choose who has SSO (user-specific).
 *
 * Mapping:
 * - ssoEnabled false → disable texts
 * - ssoEnabled true + ssoUsers null → enable (generic, user hasn't chosen yet)
 * - ssoEnabled true + ssoUsers NO_USERS → no_users texts
 * - ssoEnabled true + ssoUsers ALL_EXCEPT_ORG_ADMIN → all_except_org_admin texts
 * - ssoEnabled true + ssoUsers ALL → all_users texts
 *
 * @param values - Target form values (ssoEnabled, ssoUsers)
 * @returns Content object to pass to SSOConfirmationModal
 */
export const getSsoConfirmationModalContent = (values: {
  ssoEnabled: boolean;
  ssoUsers: SSO_USERS | null;
}): SsoConfirmationModalContent => {
  if (!values.ssoEnabled) {
    return SSO_CONFIRMATION_MODAL_TEXTS.disable;
  }
  if (values.ssoUsers === null) {
    return SSO_CONFIRMATION_MODAL_TEXTS.enable;
  }
  if (values.ssoUsers === SSO_USERS.NO_USERS) {
    return SSO_CONFIRMATION_MODAL_TEXTS.no_users;
  }
  if (values.ssoUsers === SSO_USERS.ALL_EXCEPT_ORG_ADMIN) {
    return SSO_CONFIRMATION_MODAL_TEXTS.all_except_org_admin;
  }
  return SSO_CONFIRMATION_MODAL_TEXTS.all_users;
};

/**
 * Maps API ssoExcludeRequiredRoles to form ssoUsers value.
 *
 * Inverse of the mapping in useSsoConfirmationModal formValuesToApiPayload.
 *
 * Mapping:
 * - null → NO_USERS (no users have SSO)
 * - includes ORGANIZATION_ADMIN → ALL_EXCEPT_ORG_ADMIN
 * - [] or other → ALL
 *
 * @param roles - ssoExcludeRequiredRoles from API (null or role names to exclude)
 * @returns SSO_USERS enum for the form radio group
 */
export const ssoExcludeRequiredRolesToSsoUsers = (
  roles: string[] | null,
): SSO_USERS => {
  if (roles === null) return SSO_USERS.NO_USERS;
  if (roles.includes(RoleNameEnum.ORGANIZATION_ADMIN))
    return SSO_USERS.ALL_EXCEPT_ORG_ADMIN;
  return SSO_USERS.ALL;
};

/**
 * Maps form values to API payload for PATCH /sso/configurations/{id}/manage.
 *
 * Inverse of {@link ssoExcludeRequiredRolesToSsoUsers} for the ssoUsers field.
 *
 * Mapping:
 * - ssoEnabled → ssoEnabled (direct)
 * - ssoUsers NO_USERS or null → ssoExcludeRequiredRoles null (resets on SSO toggle)
 * - ssoUsers ALL_EXCEPT_ORG_ADMIN → [ORGANIZATION_ADMIN]
 * - ssoUsers ALL → []
 *
 * @param values - Form state (ssoEnabled, ssoUsers)
 * @returns Payload for ManageSsoConfigurationRequest
 */
const ssoUsersToExcludeRoles = (ssoUsers: SSO_USERS | null): string[] | null => {
  if (ssoUsers === SSO_USERS.ALL_EXCEPT_ORG_ADMIN) return [RoleNameEnum.ORGANIZATION_ADMIN];
  if (ssoUsers === SSO_USERS.ALL) return [];
  return null;
};

export const formValuesToApiPayload = (values: SsoFormValues) => ({
  ssoEnabled: values.ssoEnabled,
  ssoExcludeRequiredRoles: ssoUsersToExcludeRoles(values.ssoUsers),
});
